import React from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { Box } from "@mui/material";

export const defaultSanitizeOptions = {
  USE_PROFILES: { html: true },
};

/**
 * Sanitizes an HTML string using DOMPurify.
 * Strips hostile scripts, event handlers, and javascript: URLs.
 */
export const sanitizeHtml = (html, options = defaultSanitizeOptions) => {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), options);
};

export const defaultHtmlSx = {
  color: "#1e293b",
  fontSize: "0.95rem",
  lineHeight: 1.6,
  wordBreak: "break-word",
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: "4px",
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    my: 2,
  },
  "& th, & td": {
    border: "1px solid #e2e8f0",
    padding: "8px 12px",
  },
  "& th": {
    backgroundColor: "#f8fafc",
    fontWeight: 600,
  },
  "& pre": {
    backgroundColor: "#f1f5f9",
    padding: "12px",
    borderRadius: "6px",
    overflowX: "auto",
    fontSize: "0.875rem",
  },
  "& code": {
    fontFamily: "monospace",
    backgroundColor: "#f1f5f9",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.875em",
  },
  "& a": {
    color: "#0284c7",
    textDecoration: "underline",
  },
  "& p": {
    mb: 1.5,
  },
  "& ul, & ol": {
    pl: 3,
    mb: 1.5,
  },
};

/**
 * Common component for safely rendering sanitized HTML markup.
 *
 * @param {Object} props
 * @param {string} [props.html] - The HTML string to sanitize and render.
 * @param {string} [props.rawHtml] - Alias for html.
 * @param {string} [props.content] - Alias for html.
 * @param {React.ReactNode} [props.children] - Fallback if html string is passed as children.
 * @param {Object} [props.sx] - Custom MUI sx styling to merge with default styles.
 * @param {boolean} [props.disableDefaultStyles] - Whether to omit default typography/content styles.
 * @param {Object} [props.options] - Custom DOMPurify options.
 * @param {string} [props.component="div"] - The root component / tag for MUI Box.
 */
const SanitizeHtml = ({
  html,
  rawHtml,
  content,
  children,
  sx = {},
  disableDefaultStyles = false,
  options = defaultSanitizeOptions,
  component = "div",
  ...rest
}) => {
  const source = html ?? rawHtml ?? content ?? (typeof children === "string" ? children : "");
  const cleanHtml = sanitizeHtml(source, options);

  const mergedSx = disableDefaultStyles ? sx : { ...defaultHtmlSx, ...sx };

  return (
    <Box
      component={component}
      sx={mergedSx}
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
      {...rest}
    />
  );
};

SanitizeHtml.propTypes = {
  html: PropTypes.string,
  rawHtml: PropTypes.string,
  content: PropTypes.string,
  children: PropTypes.node,
  sx: PropTypes.object,
  disableDefaultStyles: PropTypes.bool,
  options: PropTypes.object,
  component: PropTypes.string,
};

export { SanitizeHtml };
export default SanitizeHtml;
